import React from 'react';
import { motion } from 'motion/react';
import { Icons } from '../constants';

interface Props {
  onSelectService: (serviceType: string) => void;
  onClose: () => void;
}

const SERVICES = [
  {
    id: 'consultation',
    title: 'In-Person Arborist Consultation',
    price: 'Free Estimate',
    description: 'A certified arborist will visit your property to assess tree health, discuss your goals, and provide a detailed, itemized quote for any recommended work.',
    icon: Icons.Safety,
    color: 'bg-gecko',
    features: [
      'On-site health assessment',
      'Hazard identification',
      'Customized care plan',
      'Detailed quote'
    ]
  },
  {
    id: 'drone-survey',
    title: 'Drone Site Survey',
    price: '$149',
    description: 'Comprehensive aerial mapping of your property to identify property lines, structures, and overall canopy layout.',
    icon: Icons.Drone,
    color: 'bg-gold',
    features: [
      'High-resolution aerial maps',
      'Property line overlay',
      'Canopy density analysis',
      '3D structural modeling'
    ]
  },
  {
    id: 'drone-health',
    title: 'Drone Health Inspection',
    price: '$199',
    description: 'High-resolution multispectral imaging to detect early signs of disease, pest infestation, or nutrient deficiency.',
    icon: Icons.Tree,
    color: 'bg-palm',
    features: [
      'Multispectral imaging',
      'Early disease detection',
      'Pest infestation mapping',
      'Nutrient deficiency analysis'
    ]
  },
  {
    id: 'drone-storm',
    title: 'Emergency Storm Assessment',
    price: '$249',
    description: 'Rapid post-storm deployment to assess structural damage, hanging limbs, and immediate hazards safely.',
    icon: Icons.Dashboard,
    color: 'bg-red-500',
    features: [
      'Rapid deployment',
      'Hazard identification',
      'Damage documentation',
      'Insurance report generation'
    ]
  }
];

const ServiceCatalogView: React.FC<Props> = ({ onSelectService, onClose }) => {
  const containerVariants: any = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants: any = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
  };

  return (
    <motion.div 
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="max-w-6xl mx-auto space-y-8 animate-fade-in"
    >
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-4xl font-black text-bark tracking-tight">Service Catalog</h2>
          <p className="text-stone-400 font-bold text-lg mt-2">Select a service to begin building your project.</p>
        </div>
        <button 
          onClick={onClose}
          className="px-6 py-3 bg-white border-2 border-stone-200 text-stone-500 font-black rounded-2xl hover:bg-stone-50 transition uppercase tracking-widest text-xs"
        >
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {SERVICES.map((service) => (
          <motion.div 
            key={service.id}
            variants={itemVariants}
            className="bg-white rounded-[40px] border border-stone-200 p-10 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden flex flex-col"
          >
            <div className={`absolute top-0 right-0 p-10 opacity-5 text-bark group-hover:scale-110 transition-transform`}>
               <service.icon />
            </div>
            
            <div className="flex items-start space-x-6 mb-8 relative z-10">
              <div className={`w-16 h-16 ${service.color} rounded-2xl flex items-center justify-center text-white shadow-lg flex-shrink-0`}>
                <service.icon />
              </div>
              <div>
                <h3 className="text-2xl font-black text-bark tracking-tight">{service.title}</h3>
                <p className="text-xl font-black text-palm mt-1">{service.price}</p>
              </div>
            </div>

            <p className="text-stone-500 font-medium mb-8 flex-grow relative z-10 leading-relaxed">
              {service.description}
            </p>

            <div className="space-y-3 mb-10 relative z-10">
              <p className="text-[10px] font-black text-stone-300 uppercase tracking-widest pb-2 border-b border-stone-100">What's Included</p>
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-sm font-bold text-bark">
                    <svg className="w-4 h-4 mr-3 text-gecko" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <button 
              onClick={() => onSelectService(service.id)}
              className="w-full py-5 bg-bark text-gold rounded-3xl font-black text-sm uppercase tracking-[0.2em] hover:bg-stone-800 transition transform active:scale-95 shadow-xl relative z-10"
            >
              Select Service
            </button>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default ServiceCatalogView;
